import { Fragment, useEffect, useState } from 'react';
import { escapeRegExp } from 'utils/helpers';
import Expander from './Expander';
import FeedbackFlagWrapper from './FeedbackFlagWrapper';
import { FiPlus } from 'react-icons/fi';
import { PlaceholderText } from './Placeholders';
import { SINGULAR_NAMES } from '@/config/constants';
import { SoftButton } from './Button';
import Stack from './Stack';
import { TERM_TYPES } from './TopKeywordsSection/termTypes';
import { TermPhrase } from './TopKeywordsSection/TermComponents';
import bugsnagClient from 'containers/BugsnagClient';
import styled from 'styled-components';

// TODO: there's got to be a simpler way to do this?
const tagMatches = (str, highlight) => {
    const fallback = [{ text: str, isMatch: false }];
    if (typeof highlight !== 'string') return fallback;
    let regexp;
    try {
        regexp = new RegExp(
            `${escapeRegExp(highlight).split(/[\s]/gi).join('|')}`,
            'gi',
        );
    } catch (error) {
        bugsnagClient.notify(error);
        return fallback;
    }
    const tags = [];
    const matches = Array.from(str.matchAll(regexp)).map((arr) => ({
        match: arr[0],
        startIdx: arr.index,
        endIdx: arr.index + arr[0].length,
    }));

    if (!(matches && matches.length)) return fallback;

    matches.forEach(({ match, startIdx, endIdx }, idx) => {
        const prevMatch = idx > 0 ? matches[idx - 1] : null;
        const pre = str.slice(prevMatch ? prevMatch.endIdx : 0, startIdx);
        if (pre.length) tags.push({ text: pre });
        if (match.length) tags.push({ text: match, isMatch: true });
        if (idx === matches.length - 1 && endIdx < str.length) {
            tags.push({ text: str.slice(endIdx) });
        }
    });
    return tags;
};

// Some sections bake decoration into the display string; strip it so the
// phrase component owns it.
const stripTermDecoration = (str) =>
    typeof str === 'string'
        ? str.replace(/^[#“]+/, '').replace(/”+$/, '')
        : str;

export const getFlagData = (data) =>
    Object.entries(data || {}).reduce((acc, [key, val]) => {
        if (val && Array.isArray(val) && val[0]) {
            const singular = SINGULAR_NAMES[key];
            if (!singular)
                throw new Error(`unable to create flag data key for "${key}"`);
            acc[`${singular}Id`] = val[0];
        } else if (
            [
                'translation',
                'translationLanguage',
                'hashtag',
                'keyword',
                'dataSource',
                'relatedCategoryName',
                'relatedBrandName',
            ].includes(key)
        ) {
            acc[key] = val;
        }
        return acc;
    }, {});

function Keywords({
    phrases,
    data,
    displayQty = 3,
    highlight = null,
    translationLanguage = null,
    paginateBy = 'keywords',
    showMoreLabel,
    WrapperComponent = KeywordPhrases,
    KeywordWrapperComponent = KeywordPhraseWrapper,
    KeywordFlagWrapperComponent = KeywordFlagWrapper,
    KeywordPhraseComponent = TermPhrase,
}) {
    const [numToDisplay, setNumToDisplay] = useState(displayQty);
    useEffect(() => {
        setNumToDisplay(displayQty);
    }, [displayQty]);

    return (
        <Stack $space={(theme) => `${theme.space_5}px`}>
            <Expander>
                <WrapperComponent>
                    {phrases &&
                        phrases
                            .slice(0, numToDisplay)
                            .map(
                                ({
                                    kw: original,
                                    translations,
                                    fetching,
                                    fetchingData,
                                    id,
                                    volume,
                                    flagId,
                                    type: itemType,
                                    paginateBy: itemPaginateBy,
                                    flagData: itemFlagData,
                                }) => {
                                    const maybeTranslation = translations
                                        ? translations[translationLanguage]
                                        : null;
                                    const kw = maybeTranslation || original;
                                    // Per-item override lets a merged list flag
                                    // each phrase with its own type (keywords vs
                                    // hashtags); falls back to the section prop.
                                    const phrasePaginateBy =
                                        itemPaginateBy || paginateBy;
                                    const termType =
                                        itemType ||
                                        (phrasePaginateBy === 'hashtags'
                                            ? TERM_TYPES.HASHTAG
                                            : TERM_TYPES.KEYWORD);
                                    const bareKw = stripTermDecoration(kw);
                                    const tags = bareKw
                                        ? tagMatches(bareKw, highlight)
                                        : null;
                                    return (
                                        <KeywordWrapperComponent
                                            keywordSlot={
                                                tags ? (
                                                    <KeywordFlagWrapperComponent
                                                        flagId={flagId}
                                                        flagData={
                                                            itemFlagData ||
                                                            getFlagData({
                                                                ...data,
                                                                ...(maybeTranslation && {
                                                                    translationLanguage,
                                                                    translation:
                                                                        maybeTranslation,
                                                                }),
                                                                [phrasePaginateBy]:
                                                                    [id],
                                                                [SINGULAR_NAMES[
                                                                    phrasePaginateBy
                                                                ]]: kw,
                                                            })
                                                        }
                                                        $isTranslation={
                                                            !!maybeTranslation
                                                        }
                                                        termType={termType}
                                                        title={bareKw}
                                                    >
                                                        <KeywordPhraseComponent
                                                            termType={termType}
                                                            {...(!!maybeTranslation && {
                                                                lang: translationLanguage,
                                                            })}
                                                        >
                                                            {tags.map(
                                                                (
                                                                    {
                                                                        text,
                                                                        isMatch,
                                                                    },
                                                                    tagIdx,
                                                                ) =>
                                                                    isMatch ? (
                                                                        <Keyword
                                                                            key={
                                                                                tagIdx
                                                                            }
                                                                        >
                                                                            {
                                                                                text
                                                                            }
                                                                        </Keyword>
                                                                    ) : (
                                                                        <Fragment
                                                                            key={
                                                                                tagIdx
                                                                            }
                                                                        >
                                                                            {
                                                                                text
                                                                            }
                                                                        </Fragment>
                                                                    ),
                                                            )}
                                                        </KeywordPhraseComponent>
                                                    </KeywordFlagWrapperComponent>
                                                ) : fetching || fetchingData ? (
                                                    <KeywordPhrase>
                                                        <PlaceholderText />
                                                    </KeywordPhrase>
                                                ) : null
                                            }
                                            volume={volume}
                                            key={id}
                                        />
                                    );
                                },
                            )}
                </WrapperComponent>
            </Expander>
            <div css="display: flex; align-items: center;">
                {phrases && phrases.length > displayQty ? (
                    <SoftButton
                        IconLeft={FiPlus}
                        size="sm"
                        onClick={() =>
                            setNumToDisplay(numToDisplay + displayQty)
                        }
                        isDisabled={numToDisplay >= phrases.length}
                    >
                        Show more{' '}
                        {showMoreLabel ||
                            (paginateBy === 'keywords'
                                ? 'search queries'
                                : 'hashtags')}
                    </SoftButton>
                ) : null}
                {numToDisplay > displayQty ? (
                    <>
                        <span
                            css={`
                                border-left: solid 1px
                                    ${({ theme }) => theme.gray_5};
                                min-height: 16px;
                                margin: 0px 12px;
                            `}
                        />
                        <SoftButton
                            size="sm"
                            onClick={() => setNumToDisplay(displayQty)}
                        >
                            Hide All
                        </SoftButton>
                    </>
                ) : null}
            </div>
        </Stack>
    );
}

const KeywordPhrases = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.space_5}px;
`;

export const KeywordFlagWrapper = styled(FeedbackFlagWrapper)`
    display: inline-flex;
    align-items: center;
    background-color: ${({ theme, $isTranslation }) =>
        $isTranslation ? theme.purple_5 : theme.gray_6};
    font-style: ${({ $isTranslation }) =>
        $isTranslation ? 'oblique' : 'normal'};
    border-radius: 100rem;
    padding: ${({ theme }) => `${theme.space_3}px ${theme.space_5}px`};

    button {
        color: ${({ theme }) => theme.gray_4};
        opacity: 1;
        &:focus-visible {
            opacity: 1;
            color: ${({ theme }) => theme.purple_1};
        }
    }

    &:hover button {
        opacity: 1;
        color: ${({ theme }) => theme.purple_1};
    }
`;

const KeywordPhraseListItem = styled.li`
    margin: 0;
`;

function KeywordPhraseWrapper({ keywordSlot, volume, ...props }) {
    return (
        <KeywordPhraseListItem {...props}>{keywordSlot}</KeywordPhraseListItem>
    );
}

const KeywordPhrase = styled.span`
    line-height: 1em;
    text-transform: none;
    font-weight: ${({ theme }) => theme.fontMedium};
    color: ${({ theme }) => theme.black};

    &::before {
        color: ${({ theme }) => theme.gray_4};
        content: '\\201C';
    }
    &::after {
        color: ${({ theme }) => theme.gray_4};
        content: '\\201D';
    }
`;

const Keyword = styled.span`
    color: ${({ theme }) => theme.gray_1};
`;

export default Keywords;
